import React, { useEffect, useRef, useState, useCallback } from "react";

const InteractiveTimeline = ({ data = [], playStep = null, tailCounts = 10, onScrub }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(null); // 'move', 'left', 'right'
  const dragStartRef = useRef({ x: 0, selStart: 0, selEnd: 0 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setDimensions({
          width: entries[0].contentRect.width,
          height: entries[0].contentRect.height
        });
      }
    });
    if (wrapRef.current) observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  const totalPeriods = data.length;
  const currentStep = playStep !== null ? playStep : totalPeriods - 1;
  const endIdx = Math.min(Math.max(0, currentStep), totalPeriods - 1);
  const startIdx = Math.max(0, endIdx - tailCounts + 1);

  let selStart = totalPeriods > 1 ? startIdx / (totalPeriods - 1) : 0;
  let selEnd = totalPeriods > 1 ? endIdx / (totalPeriods - 1) : 1;

  if (selStart > selEnd) selStart = selEnd; // Safety check

  // 🔥 Canvas Drawing Engine 🔥
  useEffect(() => {
    if (!canvasRef.current || dimensions.width === 0 || dimensions.height === 0 || !data.length) return;
    const ctx = canvasRef.current.getContext("2d");
    const W = dimensions.width;
    const H = dimensions.height;
    
    // Support high DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvasRef.current.width = W * dpr;
    canvasRef.current.height = H * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, W, H);

    const padL = 10, padR = 10, padT = 5, padB = 18;
    const dW = W - padL - padR;
    const dH = H - padT - padB;

    const values = data.map(d => d.close);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const padding = range * 0.1;

    const scaleX = (i) => (i / (totalPeriods - 1)) * dW + padL;
    const scaleY = (v) => dH - ((v - (min - padding)) / (range + padding * 2)) * dH + padT;

    // Fill under line
    ctx.beginPath();
    ctx.moveTo(scaleX(0), scaleY(values[0]));
    values.forEach((v, i) => ctx.lineTo(scaleX(i), scaleY(v)));
    ctx.lineTo(scaleX(totalPeriods - 1), H - padB);
    ctx.lineTo(scaleX(0), H - padB);
    ctx.closePath();
    ctx.fillStyle = "rgba(33, 150, 243, 0.08)";
    ctx.fill();

    // Line drawing
    ctx.beginPath();
    ctx.moveTo(scaleX(0), scaleY(values[0]));
    values.forEach((v, i) => ctx.lineTo(scaleX(i), scaleY(v)));
    ctx.strokeStyle = "#2196F3";
    ctx.lineWidth = 1.3;
    ctx.stroke();

    // X-axis labels (a few dates)
    const labelCount = 6;
    ctx.fillStyle = "#888";
    ctx.font = "9px -apple-system, sans-serif";
    ctx.textAlign = "center";
    for (let i = 0; i < labelCount; i++) {
      const idx = Math.round(i * (totalPeriods - 1) / (labelCount - 1));
      if (data[idx]) {
        const px = scaleX(idx);
        const d = new Date(data[idx].date);
        const lbl = d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
        ctx.fillText(lbl, px, H - 4);
      }
    }
  }, [data, dimensions, totalPeriods]);

  // 🔥 Drag & Scrub Logic 🔥
  const handleMouseDown = (e) => {
    if (totalPeriods < 2) return;
    const wrap = wrapRef.current;
    const W = wrap.clientWidth;
    const x = e.clientX - wrap.getBoundingClientRect().left;
    const frac = x / W;
    
    const lFrac = selStart;
    const rFrac = selEnd;
    const tolerance = 12 / W;
    
    let state = null;
    if (Math.abs(frac - lFrac) < tolerance) { state = "left"; }
    else if (Math.abs(frac - rFrac) < tolerance) { state = "right"; }
    else if (frac >= lFrac && frac <= rFrac) { state = "move"; }
    else {
      // Click outside: jump selection
      const hw = (selEnd - selStart) / 2;
      let newStart = Math.max(0, frac - hw);
      let newEnd = Math.min(1, frac + hw);
      const minW = 2 / totalPeriods;
      if (newEnd - newStart < minW) newEnd = Math.min(1, newStart + minW);
      
      const newEndIdx = Math.round(newEnd * (totalPeriods - 1));
      if (onScrub) onScrub(newEndIdx);
      return;
    }
    
    setIsDragging(state);
    dragStartRef.current = { x: e.clientX, selStart, selEnd };
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || totalPeriods < 2) return;
    const W = wrapRef.current.clientWidth;
    const dx = (e.clientX - dragStartRef.current.x) / W;
    
    let newStart = dragStartRef.current.selStart;
    let newEnd = dragStartRef.current.selEnd;
    const minW = tailCounts / totalPeriods;

    if (isDragging === "move") {
      const span = dragStartRef.current.selEnd - dragStartRef.current.selStart;
      newStart = Math.max(0, Math.min(1 - span, dragStartRef.current.selStart + dx));
      newEnd = newStart + span;
    } else if (isDragging === "left") {
      newStart = Math.max(0, Math.min(newEnd - minW, dragStartRef.current.selStart + dx));
    } else {
      newEnd = Math.min(1, Math.max(newStart + minW, dragStartRef.current.selEnd + dx));
    }

    const newEndIdx = Math.round(newEnd * (totalPeriods - 1));
    if (onScrub) onScrub(newEndIdx);
  }, [isDragging, tailCounts, totalPeriods, onScrub]);

  const handleMouseUp = useCallback(() => setIsDragging(null), []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div 
      ref={wrapRef} 
      className="w-full h-full relative overflow-hidden cursor-crosshair rounded-b-md"
      onMouseDown={handleMouseDown}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      
      {/* HTML Styled Selection Overlay */}
      <div 
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{
          left: `${selStart * 100}%`,
          width: `${(selEnd - selStart) * 100}%`,
          backgroundColor: 'rgba(33, 150, 243, 0.10)',
          borderLeft: '2px solid #2196F3',
          borderRight: '2px solid #2196F3',
        }}
      >
        <div className="absolute top-0 bottom-0 left-0 -translate-x-1/2 w-[6px] pointer-events-auto cursor-col-resize" />
        <div className="absolute top-0 bottom-0 right-0 translate-x-1/2 w-[6px] pointer-events-auto cursor-col-resize" />
      </div>
    </div>
  );
};

export default InteractiveTimeline;